import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { MainLayout } from "./components/layout";
import { Dashboard } from "./pages/Dashboard";
import { Assets } from "./pages/Assets";
import { AssetDetail } from "./pages/AssetDetail";
import BrandSmartLocatePage from "./pages/BrandSmartLocate/BrandSmartLocatePage";
import BrandSmartImportPage from "./pages/BrandSmartImport/BrandSmartImportPage";
import { WarrantyPlans } from "./pages/WarrantyPlans";
import { UpgradeOffer } from "./pages/UpgradeOffer";
import { BrowseModels } from "./pages/UpgradeOffer/BrowseModels";
import { ModelDetail } from "./pages/UpgradeOffer/ModelDetail";
import { Checkout } from "./pages/UpgradeOffer/Checkout";
import { ProtectionPlans } from "./pages/UpgradeOffer/ProtectionPlans";
import { Services } from "./pages/Services";
import MaintenancePage from "./pages/Maintenance/MaintenancePage";
import { Settings } from "./pages/Settings";
import { Profile } from "./pages/Profile";
import { PaymentMethodsPage } from "./pages/PaymentMethods";
import { OrdersPage } from "./pages/Orders";
import { OrderDetailPage } from "./pages/Orders/OrderDetails";
import { BookingsPage } from "./pages/Bookings";
import { BookingDetailPage } from "./pages/Bookings/BookingDetails";
import { ClaimsPage } from "./pages/Claims";
import { Discovery } from "./pages/Discovery";
import { FlowDebugPage } from "./pages/Debug";
import { Login, Register, VerifyOTP, OAuthCallback } from "./pages/Auth";
import { Family, AcceptInvitePage } from "./pages/Family";
import OnboardingPage from "./pages/onboarding";
import TermsPage from "./pages/Legal/TermsPage";
import PrivacyPage from "./pages/Legal/PrivacyPage";
import { ROUTES } from "./config/routes";
import { PropertyProvider } from "./contexts/PropertyContext";
import { ProtectedRoute, PublicRoute, AccessGuard } from "./components/RouteGuards";
import { ScrollToTop } from "./components/ScrollToTop";
import { useInactivityMonitor } from "./hooks/useInactivityMonitor";
import { useSessionManager } from "./hooks/useSessionManager";
import { useThemeApplier } from "./hooks/useThemeApplier";
import { useBackendPreferences } from "./hooks/useBackendPreferences";
import { useTenantInit } from "./hooks/useTenantInit";
import { useThemeFavicon } from "./hooks/useThemeFavicon";
import { useDocumentTitle } from "./hooks/useDocumentTitle";
import "./pages/Auth/auth-styles.css";
import { Toaster } from "sonner";

function App() {
  // Fetch tenant config from backend on startup (slug detected from hostname)
  useTenantInit();
  // Monitor user activity and validate session
  useInactivityMonitor();
  useSessionManager({ 
    validateOnMount: true,
    // logoutOnWindowClose is configured in config/session.ts
    // Set to true there for stricter security (clear session on tab close)
  });
  // Apply theme, color preset, and font size to the DOM
  useThemeApplier();
  // Sync appearance preferences from backend (source of truth for theme persistence)
  useBackendPreferences();
  // Dynamically update favicon and document title based on active theme
  useThemeFavicon();
  useDocumentTitle();

  return (
    <PropertyProvider>
      <Toaster position="top-right" richColors closeButton />
      <ScrollToTop />
      <Routes>
        {/* Home Page - Redirect to Login */}
        <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.LOGIN} replace />} />
        
        {/* Auth Routes - Public Only (redirect to dashboard if logged in) */}
        <Route path={ROUTES.LOGIN} element={<PublicRoute><Login /></PublicRoute>} />
        <Route path={ROUTES.REGISTER} element={<PublicRoute><Register /></PublicRoute>} />
        <Route path={ROUTES.VERIFY_OTP} element={<VerifyOTP />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        
        {/* App Routes - Protected (redirect to login if not authenticated) */}
        <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
          <Route path={ROUTES.ASSETS} element={<Assets />} />
          <Route path={ROUTES.ASSET_DETAIL} element={<AssetDetail />} />
          <Route path={ROUTES.WARRANTY_PLANS} element={<WarrantyPlans />} />
          <Route path={ROUTES.UPGRADE_OFFER} element={<UpgradeOffer />} />
          <Route path={ROUTES.UPGRADE_BROWSE_MODELS} element={<BrowseModels />} />
          <Route path={ROUTES.UPGRADE_MODEL_DETAIL} element={<ModelDetail />} />
          <Route path={ROUTES.UPGRADE_CHECKOUT} element={<Checkout />} />
          <Route path={ROUTES.UPGRADE_PROTECTION_PLANS} element={<ProtectionPlans />} />
          <Route path={ROUTES.SERVICES} element={<AccessGuard serviceType="bookings"><Services /></AccessGuard>} />
          <Route path={ROUTES.MAINTENANCE} element={<AccessGuard serviceType="maintenance"><MaintenancePage /></AccessGuard>} />
          <Route path="/discovery" element={<Discovery />} />
          <Route path={ROUTES.ORDERS} element={<AccessGuard serviceType="orders"><OrdersPage /></AccessGuard>} />
          <Route path={ROUTES.ORDER_DETAIL} element={<AccessGuard serviceType="orders"><OrderDetailPage /></AccessGuard>} />
          <Route path={ROUTES.BOOKINGS} element={<AccessGuard serviceType="bookings"><BookingsPage /></AccessGuard>} />
          <Route path={ROUTES.BOOKING_DETAIL} element={<AccessGuard serviceType="bookings"><BookingDetailPage /></AccessGuard>} />
          <Route path={ROUTES.CLAIMS} element={<ClaimsPage />} />
          <Route path="/debug/flow" element={<FlowDebugPage />} />
          <Route path={ROUTES.SETTINGS} element={<Settings />} />
          <Route path="/profile" element={<Profile />} />
          <Route path={ROUTES.PAYMENT_METHODS} element={<PaymentMethodsPage />} />
          <Route path={ROUTES.FAMILY} element={<Family />} />
          {/* BrandSmart Import — inside MainLayout (uses app navbar) */}
          <Route path="/brandsmart/import" element={<BrandSmartImportPage />} />
        </Route>
        {/* Invite accept — public, outside MainLayout (handles own auth redirect) */}
        <Route path={ROUTES.INVITE_ACCEPT} element={<AcceptInvitePage />} />
        {/* BrandSmart Locate — protected, full-page (outside MainLayout) */}
        <Route path="/brandsmart/locate" element={<ProtectedRoute><BrandSmartLocatePage /></ProtectedRoute>} />
        {/* Onboarding — protected, outside MainLayout (user has no home yet) */}
        <Route path={ROUTES.ONBOARDING} element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        {/* Legal — public, no auth required */}
        <Route path={ROUTES.TERMS} element={<TermsPage />} />
        <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
      </Routes>
    </PropertyProvider>
  );
}

export default App;
